// Homepage header overlay controller (the header is fixed over the hero and takes no layout space).
// - Keeps data-transparent while the hero ([data-header-hero] in main) is still under the header,
//   and drops it once the visitor scrolls past the hero, so the header turns solid (white, black ink).
// - Ink over the hero comes from the header block's "Logo and navigation over hero" setting
//   (data-overlay-ink on the header): "light" = white logo/nav, "dark" = black logo/nav.
// - Sets data-ink="light" only while transparent, the ink setting is light, and the mega menu is closed.
class SHeaderScroll extends HTMLElement {
  #controller = null;
  #frame = 0;

  connectedCallback() {
    const header = this.closest('header');
    if (!header) return;
    const hero = document.querySelector('main [data-header-hero]');
    const fallbackThreshold = Number(this.dataset.threshold || 24);
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    let megaOpen = false;

    const update = () => {
      this.#frame = 0;
      const transparent = hero
        ? hero.getBoundingClientRect().bottom > header.offsetHeight
        : window.scrollY <= fallbackThreshold;
      header.toggleAttribute('data-transparent', transparent);
      // A hero can override the header setting with data-hero-ink="light|dark" when its image needs a specific ink.
      // data-hero-ink-mobile overrides it below md (e.g. a hero whose phone image starts below the header on a white band).
      const mobileInk = window.matchMedia('(max-width: 767.98px)').matches ? hero?.dataset.heroInkMobile : undefined;
      const ink = mobileInk || hero?.dataset.heroInk || header.dataset.overlayInk;
      const lightInk = ink !== 'dark';
      if (transparent && !megaOpen && lightInk) header.setAttribute('data-ink', 'light');
      else header.removeAttribute('data-ink');
    };
    const schedule = () => {
      if (!this.#frame) this.#frame = requestAnimationFrame(update);
    };

    header.addEventListener('mega-menu:toggle', (event) => {
      megaOpen = Boolean(event.detail?.open);
      update();
    }, { signal });
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });

    // Re-apply when the ink setting changes in the editor without a full reload.
    const observer = new MutationObserver(update);
    observer.observe(header, { attributes: true, attributeFilter: ['data-overlay-ink'] });
    signal.addEventListener('abort', () => observer.disconnect());

    update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }
}

if (!customElements.get('s-header-scroll')) customElements.define('s-header-scroll', SHeaderScroll);
